/**
  * @class meleDataList
  * @description
  *
  * # Purpose
  * Implements `<mele-datalist>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-datalist></mele-datalist>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-datalist',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleList from 'meleList'

const PROPERTIES = _.extend({}, {
  new: { type: 'boolean', description: 'if set, + button will be created to allow new item creation' },
}, meleList._properties)

export default class meleDataList extends meleList {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()
    this.key_parameter = 'id'
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "auto"
  */
  newChanged() {
    if(this.new) {
      this.headerbuttons = ['new']
    }else {
      this.headerbuttons = []
    }
  } // newChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Bind detail element to the list
  */
  bind(elDetail, key_parameter) {
    this.elDetail = elDetail
    this.key_parameter = key_parameter || this.key_parameter || 'id'

    elDetail.unavailable = true

    elDetail.eventValueChanged = (el, value, old, context_user_interaction)=>{
      const change = {}
      change[el.key] = el.value
      const datakey = elDetail.datakey
      const selected_item = this._body.find('>@{'+datakey+'}')
      if(selected_item) {
        selected_item.data = _.extend({}, selected_item.data, change)
        this.eventValueChanged(el, value, old, context_user_interaction)
      }
    }

    const doSubmit = ()=>{
      const values = elDetail.getChangedValues()
      if(!_.isObjectLike(values)) return

      // ensure key parameter exists in returned values
      if(_.isNil(values[this.key_parameter])) {
        values[this.key_parameter] = elDetail.datakey
      }
      if(_.toString(values[this.key_parameter])=='') {
        delete values[this.key_parameter]
      }

      this.eventSubmit(elDetail, elDetail.datakey, values)
    }

    elDetail.eventSubmit = ()=>{
      if(elDetail.conflict) {
        this.modalConfirm('There are conflicting changes, overwrite them?', doSubmit)
      }else{
        doSubmit()
      }
    }

    const doDelete = ()=>{
      this.eventDelete(elDetail, elDetail.datakey)
    }

    elDetail.eventDelete = (el, datakey)=>{
      if(_.isNil(datakey)) return
      datakey = _.toString(datakey)
      if(datakey=='') {
        if(el.changed) {
          this.modalConfirm('This will discard your changes. Are you sure?', doDelete)
        }else {
          doDelete()
        }
      }else{
        this.modalConfirm('Are you sure?', doDelete)
      }
    }
  } // bind

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Update datalist records
  */
  update(params) {
    let list = params.list
    if(!Array.isArray(list)) return
    const full = !!params.full

    if(full) {
      // delete all items but new, i.e. one without key
      this._body.forAll('>[key]:not([key=""])', el=>el.deleteElement())
    }

    // get key of item currently edited in detail view
    const datakey = this.elDetail ? this.elDetail.datakey : null

    // create mele-item definitions
    let form_data
    list = list.map(item=>{
      const key = item[this.key_parameter]
      if(!_.isNil(datakey) && key==datakey) {
        form_data = item
      }
      return({
        key,
        data: item,
      })
    })

    this.updateItems(list)

    // if also item displayed in binded detail is changed, update the detail too
    if(this.elDetail && form_data) {
      this.elDetail.setServerValues(form_data)
    }

    // select first list item if current selection is invalid (i.e. empty or pointing to non-existing item)
    // this applied only when we have at least one item
    if(this.items.length > 0) {
      this.elDetail.unavailable = false
      const selected = this.selected[0]
      if(_.isNil(selected) || !this.getItem(selected)) {
        const first = this._body.find('>*')
        this.selected = first ? _.toString(first.key) : null
      }
    }else{
      this.elDetail.unavailable = true
      this.elDetail.formClear()
    }
  } // update

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * remove servervalues from the binded form
  */
  commit(params) {
    if(!this.elDetail) return
    this.elDetail.commitValues()
  } // commit

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * delete item from list; selects nearest sibling if deleted item was selected
  */
  delete(key) {
    if(this.items.length==0) {
      this.elDetail.setValues()
      return
    }

    key = _.toString(key)

    // find index of the deleted item if it is selected
    const selected = this.selected
    const new_selected = selected.filter(k=>k!=key)
    let new_idx = null
    if(selected.length>0 && new_selected.length==0) {
      // removed item is selected and after its removal the selection would be empty - so selec item coming to its position in the list
      new_idx = 0
      for(let i=0; i<this._body.children.length; i++) {
        const child = this._body.children[i]
        if(_.toString(child.key) == key) {
          new_idx = i
          break
        }
      }
    }

    this.deleteItems(key)

    if(new_idx != null) {
      // select item coming to the same position as deleted item
      const children = this._body.findAll('>:not([key=":comber"])')
      const count = children.length
      if(new_idx>=count) {
        new_idx=count-1
      }
      if(new_idx<0) {
        // this.elDetail.setValues()
        this.selected = []
      }else{
        this.selected = [children[new_idx].key]
      }
    }
  } // delete

  deleteItems(items) {
    super.deleteItems(items)
    this._refreshUnavailable()
  }

  _refreshUnavailable() {
    const existing = this.selected.map(key=>!!this._body.find('>@{'+key+'}')).length
    this.elDetail.unavailable = existing==0
    if(this.elDetail.unavailable) this.elDetail.formClear()
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * change selection as required with optional confirmation dialog if confirmselect is set to non-empty string
  * @param {array} selected - items to be selected
  * @param {any} context_user_interaction -
  */
  changeSelected(selected, context_user_interaction, force_if_notchanged) {
    if(this.elDetail && _.toString(this.elDetail.datakey)=='') {
      this.modalConfirm('This will discard your changes. Are you sure?', ()=>{
        this.modalCloseDefault()
        this.deleteItems('')
        super.changeSelected(selected, context_user_interaction, force_if_notchanged)
      })
    }else if(this.elDetail && this.elDetail.changed) {
      this.modalConfirm('This will discard your changes. Are you sure?', ()=>{
        this.modalCloseDefault()
        this.elDetail.formUndo()
        super.changeSelected(selected, context_user_interaction, force_if_notchanged)
      })
    }else{
      super.changeSelected(selected, context_user_interaction, force_if_notchanged)
    }
  } // changeSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * notification of changed "selected"
  */
  eventSelected(self, items, old, context_user_interaction) {
    if(this.elDetail) {
      const key = items[0]
      const item = this.getItem(key)
      this.elDetail.datakey = key
      this.elDetail.unavailable = _.isNil(key)
      if(this.elDetail.unavailable) this.elDetail.formClear()
      if(item) {
        this.elDetail.setValues(item.data)
        this.elDetail.scrollTop()
        const elWindow = this.closest('.mele-window')
        if(context_user_interaction) {
          const idx = _.toString(elWindow.views).split(',').indexOf(this.elDetail.key)
          if(idx>=0) elWindow.active = idx
        }
      }else{
        this.elDetail.setValues()
      }
    }
  } // eventSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * clicked "+" button
  */
  formNew() {
    if(this.disabled) return
    if(this.elDetail && this.elDetail.changed) {
      this.modalConfirm('This will discard your changes. Are you sure?', ()=>{
        this.modalCloseDefault()
        this.elDetail.formUndo()
        this.updateItems('')
        this.selected = ['']
        this.eventNew(this)
        this.elDetail.scrollTop()
      })
    }else{
      this.updateItems('')
      this.selected = ['']
      this.eventNew(this)
      this.elDetail.scrollTop()
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * notification of clicked "+" button
  */
  eventNew(self) {
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * notification of clicked "+" button
  */
  eventOcr(self) {
  }
} // class meleDataList

meleDataList._properties = PROPERTIES
meleDataList.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-datalist', meleDataList)
